/**
 * What the account area's cloud routes answer with (§5.25) — for the page, which
 * a signed-in browser uses, and for the app, which holds a device token.
 *
 * Written once here rather than twice, because three consumers read these shapes
 * — the Worker that writes them, the page and the app that read them — and a
 * field renamed on one side and not another is a sync that silently stops.
 */
import type { OrgSettings } from '../org/settings';
import type { RevisionDiff } from '../workspace/diff';
import type { OrgWorkflows, WorkspaceDocument } from '../workspace/document';

/** The header the app names its machine in, on every device-token call as on every gate call (§13). */
export const MACHINE_HEADER = 'x-airlock-machine';

/** A device token unused this long is refused, and the machine links again. */
export const DEVICE_TOKEN_IDLE_DAYS = 90;

/** How many projects one organization keeps. */
export const MAX_WORKSPACES_PER_ORG = 200;

/** How long a publish's message may be — a commit subject, not a changelog. */
export const MAX_PUBLISH_MESSAGE_CHARS = 500;

export type OrgRole = 'owner' | 'admin' | 'member';

/** The two seat states that let a member in. `lapsed` and `none` answer 404, as a stranger does. */
export type CloudAccess = 'active' | 'grace';

export interface CloudOrg {
  id: string;
  name: string;
  slug: string;
  role: OrgRole;
  access: CloudAccess;
}

/** `GET /api/cloud/me` — who this token acts as, and in which organizations. */
export interface CloudMe {
  user: { id: string; email: string; name: string | null };
  orgs: CloudOrg[];
}

/** Somebody named in a trail. `id` is null once the account was anonymised; `label` still reads. */
export interface Actor {
  id: string | null;
  label: string;
}

/** Which machine did it, when an app did — `null` for the browser. */
export interface DeviceRef {
  label: string | null;
}

export interface WorkspaceSummary {
  id: string;
  orgId: string;
  slug: string;
  name: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: Actor;
  updatedBy: Actor;
  archivedAt: string | null;
  /**
   * Two facts read off the document so that a list does not have to open one.
   *
   * The colour is the workspace's own, the same swatch the app puts beside it,
   * and the count is how many projects it carries. A list that had to fetch a
   * megabyte of document per row to draw a nine-pixel square would not draw it,
   * and then the list would be seven identical rows of text.
   */
  color: string;
  projects: number;
  /**
   * What the caller may do here, and what gives it to them.
   *
   * The second half is the whole point of stating it: an administrator reads
   * this list to see who looks after a workspace, and somebody holding a seat
   * reads it to see whether they may touch one — which is a question about
   * *their* grant, not about the workspace. `via` is the team's name, or the
   * words for a grant made to them by name, or for administering the
   * organization itself.
   */
  access: { right: WorkspaceRight; via: string };
}

/* ── teams, and what a workspace is shared with ─────────────────────────── */

/**
 * What a grant lets somebody do, weakest first.
 *
 * They nest: `write` reads, `admin` writes. The third one moves something that
 * used to belong to the organization alone — archiving a workspace, restoring
 * it, and deciding these rows — so a workspace can now name its own keepers
 * without making them administrators of everything.
 */
export type WorkspaceRight = 'read' | 'write' | 'admin';

export const WORKSPACE_RIGHTS: readonly WorkspaceRight[] = ['read', 'write', 'admin'];

/** Somebody reached by two grants keeps the stronger. Never the later one. */
export function strongerRight(a: WorkspaceRight, b: WorkspaceRight): WorkspaceRight {
  return WORKSPACE_RIGHTS.indexOf(a) >= WORKSPACE_RIGHTS.indexOf(b) ? a : b;
}

/** Whether a right is at least this strong. */
export function rightAllows(held: WorkspaceRight, needed: WorkspaceRight): boolean {
  return WORKSPACE_RIGHTS.indexOf(held) >= WORKSPACE_RIGHTS.indexOf(needed);
}

export interface TeamSummary {
  id: string;
  name: string;
  members: number;
  /** How many of the organization's workspaces this team reaches. */
  workspaces: number;
  createdBy: Actor | null;
  createdAt: string;
}

/** One person in a team. Their seat is stated because it is a *different* fact:
 *  a team grants no licence, and being in one says nothing about holding a seat. */
export interface TeamMember {
  userId: string;
  email: string;
  name: string | null;
  role: OrgRole;
  seatStatus: string;
  since: string;
}

export interface TeamRecord {
  team: TeamSummary;
  members: TeamMember[];
  /** Seats not in this team yet — who there is left to add. */
  candidates: TeamMember[];
}

export interface TeamList {
  teams: TeamSummary[];
}

/** One row of a workspace's access: a team **or** a person, and what they may do. */
export interface WorkspaceGrant {
  id: string;
  team: { id: string; name: string; members: number } | null;
  person: { id: string; email: string; name: string | null } | null;
  right: WorkspaceRight;
  grantedBy: Actor | null;
  grantedAt: string;
}

export interface WorkspaceGrantList {
  grants: WorkspaceGrant[];
}

/** `PUT …/workspaces/:id/grants` — a team or a person, and the right they get. */
export interface GrantRequest {
  teamId?: string;
  userId?: string;
  right: WorkspaceRight;
}

/* ── what an organization looks like at a glance ─────────────────────────── */

/**
 * The overview's five counters, and the three lists under them.
 *
 * They answer **one** question — *does somebody who joins today arrive at
 * something ready?* — and each says what is **missing** rather than settling
 * for a number, because a number alone is a total and the thing being asked for
 * is an answer.
 *
 * These are the only figures the area carries. Nothing in the navigation counts
 * anything any more; here the counting *is* the content, which is the whole
 * difference between a decorative total and something worth opening in the
 * morning.
 */
export interface OrgOverview {
  /** What a new member's install is handed at its first link, without them setting anything. */
  handed: {
    /** Shared with everybody, out of the workspaces that are not archived. */
    workspaces: { ready: number; total: number };
    /** Of the settings an organization may answer for, and how many it holds shut. */
    settings: { decided: number; of: number; locked: number };
    /** Which agents a member may launch, and how many carry a configuration. */
    agents: { offered: string[]; configured: number };
    integrations: { connected: string[]; off: string[] };
    /**
     * Four numbers, because a member and a seat stopped being the same thing:
     * `held` is people holding one of the organization's seats, `seatless`
     * people who are here without one, `invited` addresses that have not
     * answered — and `bought` is what the subscription pays for.
     */
    seats: { held: number; bought: number; invited: number; seatless: number };
  };
  /** The newest versions published, whatever they were published to. */
  published: AuditEntry[];
  /**
   * What is waiting for a person. Each names the page that answers it, because
   * a list of problems with nowhere to go is a list nobody acts on.
   */
  waiting: { id: string; what: string; why: string; page: string }[];
  billing: {
    seats: number;
    perSeatUsd: number | null;
    plan: string | null;
    /** When the next invoice falls, `null` for an organization that buys nothing. */
    nextAt: string | null;
  };
}

/**
 * `GET /api/orgs/:id/overview/mine` — the same page for somebody who
 * administers nothing: not the organization's morning, but which of its
 * workspaces they work in.
 *
 * Read off the trail rather than a table of its own, because the trail already
 * has it: an install that joins reports `project.join` (`recordInstallEvent`),
 * so a second record of the same fact could only ever disagree with this one.
 * A member reads their **own** rows and no others — who else joined what is
 * information about the organization, which is an administrator's page.
 */
export interface MemberJoins {
  /**
   * One row per workspace this member has joined, the latest join first.
   *
   * A workspace that has since been archived is not one of them: nobody pulls
   * an archived workspace, so a row for it would say somebody works somewhere
   * they cannot.
   */
  joins: { projectId: string; at: string }[];
}

/** That a secret has a value, and who gave it one — never the value. */
export interface SecretPresence {
  path: string;
  updatedAt: string;
  updatedBy: Actor;
}

/**
 * Two spellings of one field, and why there are two.
 *
 * What the account publishes used to be called a project and is called a
 * **workspace** now. The types, the tables, the routes and every word a person
 * reads were renamed; these JSON keys were not, because every copy of Airlock
 * already installed reads `project` and `projects` off these answers, and a
 * document whose fields it does not recognise is a document it refuses. A
 * rename here would be our tidiness bought with their broken pull.
 *
 * So the Worker answers both, the newer half of the pair is what new code
 * reads, and `project`/`projects` go the release after the last install that
 * reads them is gone — not before.
 */
export interface WorkspaceRecord {
  workspace: WorkspaceSummary;
  /** @deprecated The same object as `workspace`, for installs that predate the rename. */
  project: WorkspaceSummary;
  document: WorkspaceDocument;
  secrets: SecretPresence[];
  /**
   * Who seals this workspace's values: the Worker (`server`), or the members'
   * machines under the organization key (`e2e`, §5.25). Absent from a Worker
   * that predates end-to-end encryption, which is `server`. Under `e2e` the
   * page cannot set a value — it has no key — and says so; it can still remove
   * one.
   */
  sealing?: 'server' | 'e2e';
}

/**
 * Path → value, or `null` to take the value away. Under the Worker's sealing a
 * value is the plaintext it seals; under end-to-end encryption it is the
 * `e1.` ciphertext the publishing machine sealed (`e2e.ts`), and plaintext is
 * refused.
 */
export type SecretWrites = Record<string, string | null>;

/** `POST …/projects` — a workspace published for the first time. */
export interface CreateWorkspaceRequest {
  document: WorkspaceDocument;
  secrets?: SecretWrites;
  message?: string;
  /**
   * The workspace's id, chosen by the machine (`prj_` and 8 to 40 characters):
   * a value sealed end to end names its workspace in its additional data, so
   * the id has to exist before the value is sealed. Optional otherwise.
   */
  id?: string;
  /** The organization key's generation the sealed values were made with. Required with sealed values. */
  generation?: number;
}

/** `POST …/projects/:pid/publish`, sent with `If-Match: <version>`. */
export interface PublishRequest {
  document: WorkspaceDocument;
  secrets?: SecretWrites;
  message?: string;
  /** As on `CreateWorkspaceRequest`. */
  generation?: number;
}

export interface PublishResponse {
  workspace: WorkspaceSummary;
  /** @deprecated The same object as `workspace` — see `WorkspaceRecord`. */
  project: WorkspaceSummary;
  diff: RevisionDiff;
}

/** The 409 a stale `If-Match` gets: where the project is now, and what arrived since the caller's version. */
export interface PublishConflict {
  error: string;
  current: WorkspaceSummary;
  diff: RevisionDiff;
}

/** The 422 a document `parseWorkspaceDocument` refused gets. */
export interface DocumentRefusal {
  error: string;
  problems: string[];
  newer: boolean;
}

export interface RevisionSummary {
  version: number;
  author: Actor;
  device: DeviceRef | null;
  at: string;
  message: string | null;
  changes: number;
}

export interface RevisionDetail extends RevisionSummary {
  document: WorkspaceDocument;
  diff: RevisionDiff;
}

export type CloudChangeKind =
  | 'project.create'
  | 'project.publish'
  | 'project.archive'
  | 'project.restore'
  | 'settings.publish'
  | 'workflows.publish';

/** One line of `GET /api/cloud/changes`: something a member's install may have to act on. */
export interface CloudChange {
  cursor: string;
  at: string;
  orgId: string;
  projectId: string | null;
  kind: CloudChangeKind;
  version: number | null;
  actor: Actor;
}

export interface ChangesResponse {
  changes: CloudChange[];
  /** Where the next read starts. Returned even when nothing changed, so a first read has one. */
  cursor: string;
  /** More than one page arrived since the cursor; read again. */
  more: boolean;
}

/** `GET …/projects/:pid/secrets` — the one read that hands values out, to a device token alone. */
export interface SecretValues {
  secrets: Record<string, { value: string; updatedAt: string }>;
}

export interface OrgSettingsRecord {
  settings: OrgSettings;
  version: number;
  updatedAt: string | null;
  updatedBy: Actor | null;
}

/** `GET /cloud/orgs/:id/workflows` — the organization's workflows as they stand. No row is version 0: none shared. */
export interface OrgWorkflowsRecord {
  document: OrgWorkflows;
  version: number;
  updatedAt: string | null;
  updatedBy: Actor | null;
}

/** `PUT …/workflows`, sent with `If-Match: <version>` — by the page, and by an install publishing its own. */
export interface OrgWorkflowsRequest {
  document: OrgWorkflows;
}

/** The 409 a stale `If-Match` on the workflows gets. */
export interface OrgWorkflowsConflict {
  error: string;
  current: OrgWorkflowsRecord;
}

export const AUDIT_ACTIONS = [
  'project.create',
  'project.publish',
  'project.archive',
  'project.restore',
  'project.join',
  'project.pull',
  'secret.set',
  'secret.remove',
  'secret.read',
  'settings.publish',
  'workflows.publish',
  'token.issue',
  'token.revoke',
  'token.mismatch',
  /** An owner made a member an administrator (`subject` is who). */
  'member.promote',
  /** An owner made an administrator a member again. */
  'member.demote',
  /** The owner handed the organization to another member (`subject` is who took it). */
  'org.transfer',
  /** An owner or admin cut a member's machine off from this organization (`device` the token, `subject` the member). */
  'machine.block',
  /** …and let it back in. */
  'machine.unblock',
  /** An owner or admin freed one machine's slot on a member's seat licence, at the gate. */
  'machine.release',
  // End-to-end encryption (`e2e.ts`): a machine's public key registered, the
  // organization key wrapped for a machine, the switch away from the Worker's
  // sealing, and a new generation of the key; `key.vouch` is a machine that
  // already held the key wrapped again with a key holder's vouch for it.
  'key.register',
  'key.approve',
  'key.vouch',
  'key.switch',
  'key.rotate',
  // A monthly budget set or removed on the Spend page: its scope in `paths`,
  // the dollars in `count` — a figure, never a value.
  'budget.set',
  'budget.remove',
  // An endpoint the audit and the runs are streamed to, added, switched on or
  // off, or removed: its host in `paths` — never the address whole, nor its secret.
  'stream.create',
  'stream.update',
  'stream.remove',
  /** An owner or admin added a domain to prove (`detail.paths` is the domain, Enterprise). */
  'domain.add',
  /** …and its DNS record proved it: the domain is this organization's alone. */
  'domain.verify',
  /** …or took it away, verified or not. */
  'domain.remove',
  /** An owner or admin changed the identity policy: who a verified domain lets in, what a sign-in must show. */
  'identity.policy',
  /** Somebody joined as a member through a verified domain or single sign-on (`subject` is who). */
  'member.join',
  /** An owner or admin saved the single sign-on connection (its protocol, switches or provider). */
  'sso.update',
  /** …or removed it. */
  'sso.remove',
  /** An owner or admin issued a SCIM provisioning token. */
  'scim.token.issue',
  /** …or revoked one. */
  'scim.token.revoke',
  /** The identity provider created or reactivated a member (actor: who issued the token; `subject` the member, once they have an account). */
  'scim.provision',
  /** …deactivated or deleted one — the membership is suspended, never deleted. */
  'scim.deprovision',
  /** …created, changed or deleted a team. */
  'scim.group',
  // The organization's GitHub installation (`github-app.ts`): linked from the
  // page, unlinked, and a token minted for one workspace's repositories on a
  // member's machine (`paths` the repositories, never the token).
  'github.link',
  'github.unlink',
  'github.token',
  /** An owner or admin approved a repository for the installation's tokens (`detail.paths` its `owner/name`; account `github-repos.ts`, SEC-04). */
  'github.repo.approve',
  /** …or took one off that list. */
  'github.repo.revoke',
  /** The owner moved the subscription between monthly and annual (`detail.paths` the plan it moves to; account `billing-period.ts`). */
  'billing.period',
  /** …or called off a move to monthly still waiting for the paid year to end. */
  'billing.period.cancel',
  /** A change to a rules repository proposed from the page, as a pull request its GitHub App opened (§5.27; `detail.paths` the repository then each file, `detail.version` the pull request's number). */
  'rules.pull'
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Paths and versions, never a value: what `org_audit.detail` is allowed to hold. */
export interface AuditDetail {
  paths?: string[];
  version?: number;
  from?: number;
  count?: number;
}

export interface AuditEntry {
  id: string;
  at: string;
  actor: Actor;
  device: DeviceRef | null;
  action: AuditAction;
  projectId: string | null;
  projectName: string | null;
  version: number | null;
  detail: AuditDetail;
  country: string | null;
  /**
   * The person the act was done to — a role changed, an organization handed
   * over, a machine cut off — and `null` where it was done to a thing. Optional
   * because an answer written before the column existed carries none.
   */
  subject?: Actor | null;
}

export interface AuditPage {
  entries: AuditEntry[];
  cursor: string | null;
  /** How many lines the filters match in all, every page counted. */
  total: number;
}

/** `POST …/projects/:pid/events` — what an install tells the trail it did with a version. */
export interface CloudEventRequest {
  action: 'project.join' | 'project.pull';
  version: number;
}

/** A device token as the Machines page lists it. The token itself is never shown again. */
export interface DeviceTokenRow {
  id: string;
  label: string | null;
  machine: string;
  createdAt: string;
  lastUsedAt: string;
  revokedAt: string | null;
}

/**
 * `GET /api/orgs/:id/machines` — every linked machine of the organization's
 * members, as its owner and administrators see them. Two lists, because they
 * are two credentials: the device tokens that read and publish the
 * organization's workspaces, and the machines each seat's licence key occupies
 * at the gate.
 */
export interface OrgMachineToken {
  /** The first 16 hex characters of the token's digest — what the routes below name it by. */
  id: string;
  /** The membership it belongs to, and the member's address. */
  seatId: string;
  email: string;
  label: string | null;
  /** The first 12 characters of the machine's fingerprint. */
  machine: string;
  country: string | null;
  createdAt: string;
  lastUsedAt: string;
  /** Revoked by its holder, by an idle expiry or by an account closed — everywhere. */
  revokedAt: string | null;
  /** Cut off from this organization only, and when. */
  blockedAt: string | null;
}

export interface OrgMachineSlot {
  seatId: string;
  email: string;
  serial: number;
  /** The whole fingerprint: it is what freeing the slot names. */
  fingerprint: string;
  label: string | null;
  firstSeen: number;
  lastSeen: number;
  country: string | null;
}

export interface OrgMachines {
  tokens: OrgMachineToken[];
  slots: OrgMachineSlot[];
  /** Why the slots could not be read — the gate unreachable or not configured — or `null`. */
  slotsProblem: string | null;
}

/* ── linking a machine (§13), and the link that skips typing the code (§5.25) ── */

/** `POST /api/device/start`, and `POST /api/device/claim` — a grant this machine now holds the secret of. */
export interface DeviceGrantStarted {
  userCode: string;
  deviceCode: string;
  /** The approval page, which the app opens in the browser. */
  url: string;
  expiresAt: number;
  /**
   * For a grant claimed from an offered link (`POST /api/device/claim`): six
   * digits the app shows and the person types on the approval page. The offer's
   * account cannot approve the claim without them, so a link somebody else
   * planted cannot hand this machine their licence and device token — they
   * never see this screen. Absent for a grant the app started itself, and from
   * an older Worker.
   */
  confirmCode?: string;
  /** The account that offered the link — what the app names before it asks for the code. Absent otherwise. */
  offeredBy?: string | null;
}

/**
 * `POST /api/device/offer` — a code the signed-in page creates for its own
 * account and hands to `airlock://link?code=…`. It is not a credential: an app
 * that claims it still has to be approved, in that browser, by that account.
 */
export interface DeviceOffer {
  userCode: string;
  expiresAt: number;
  link: string;
}

/** `POST /api/device/claim` — the app that arrived by an offer's link, taking its grant. */
export interface DeviceClaimRequest {
  userCode: string;
  label?: string;
  /**
   * `true` from an app that asked the person before claiming and shows
   * `confirmCode`. A claim without it is refused (400): an older app claims and
   * adopts silently, and refusing it is how it degrades safely.
   */
  confirm?: boolean;
}

/**
 * `POST /api/device/poll`. `token` is the device token (§5.25), and it is only
 * ever present for an account that holds a seat in an organization — a personal
 * account is handed its key and nothing else, exactly as before.
 */
export type DevicePoll =
  | { status: 'pending' }
  | { status: 'denied' }
  | { status: 'expired' }
  | {
      status: 'approved';
      licensed: boolean;
      key: string | null;
      serial: number | null;
      tier: string | null;
      token: string | null;
      /**
       * With no key to hand over: the organization this account is in without
       * a seat, so the app can say who gives one rather than offering to sell
       * a licence. Absent from an older Worker, and null when there is none.
       */
      seatless?: { organization: string } | null;
      /**
       * The address of the account that approved the grant, so the app can name
       * whose licence and organization it is about to take before it replaces
       * one it already holds. Absent from an older Worker.
       */
      account?: string | null;
    };

/** The six digits of an offered grant's confirmation code, as the approval page sends them (spaces dropped). */
export const DEVICE_CONFIRM_CODE = /^[0-9]{6}$/;

/** The eight characters of a user code, in the alphabet `grants.ts` draws from — all a link may carry. */
export const DEVICE_USER_CODE = /^[23456789ABCDEFGHJKLMNPQRSTVWXYZ]{8}$/;

/* ── an organization's settings, as the page edits them (§5.25) ─────────── */

/** `PUT /api/orgs/:id/settings`, sent with `If-Match: <version>`. */
export interface OrgSettingsRequest {
  settings: OrgSettings;
}

/** The 409 a stale `If-Match` on the settings gets. */
export interface OrgSettingsConflict {
  error: string;
  current: OrgSettingsRecord;
}

/**
 * `GET /api/orgs/:id/settings/reach` — who a publish of these settings lands on.
 *
 * A publish writes a whole version that every install reads at its next check,
 * and one of the things it can write is a **lock** — the single act in the
 * product that takes something away from somebody else, on their machine,
 * without their asking. `enforced` can freeze twenty-six settings across every
 * install at once, so publishing blind is the real risk on that surface, far
 * more than a refused document is.
 *
 * Which is why this is a query and not an estimate. The Worker knows which
 * machines hold a live token and which workspace documents carry the same key,
 * so "2 workspaces override it: docs-site (32768) and infra (24576) will stop
 * getting their value" is computable, and a dialog that guessed at it would be
 * worse than one that said nothing.
 */
export interface OrgSettingsReach {
  /** Machines of assigned members holding a token that has not been revoked. */
  installs: number;
  /**
   * The workspaces that answer a key for themselves, by key.
   *
   * Only the keys some workspace overrides appear, and only workspaces that are
   * not archived: an archived one is not pulled by anybody, so a lock cannot
   * take anything away from it.
   */
  overrides: Record<string, { id: string; name: string; value: unknown }[]>;
}

/** One line of `GET /api/orgs/:id/settings/revisions`. */
export interface OrgSettingsRevision {
  version: number;
  author: Actor;
  device: DeviceRef | null;
  at: string;
  diff: RevisionDiff;
}

/** Lists, as every list route wraps them — an object, so a field can be added without breaking a reader. */
export interface WorkspaceList {
  workspaces: WorkspaceSummary[];
  /** @deprecated The same array as `workspaces` — see `WorkspaceRecord`. */
  projects: WorkspaceSummary[];
}

export interface RevisionList {
  revisions: RevisionSummary[];
}

export interface OrgSettingsRevisionList {
  revisions: OrgSettingsRevision[];
}

export interface SecretPresenceList {
  secrets: SecretPresence[];
}

export interface DiffResponse {
  diff: RevisionDiff;
}
